import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  Cake as CakeIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import axios from 'axios';
import MedicalRecordsService from '../../services/MedicalRecordsService';
import PatientService from '../../services/PatientService';
import AppointmentService from '../../services/AppointmentService';

const PatientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Mock data for related information
  const [appointments, setAppointments] = useState([]);
  const [medicalRecords, setMedicalRecords] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  
  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await PatientService.getPatientById(id);
        const p = response.data || null;
        if (!p) {
          setPatient(null);
        } else {
          setPatient({
            patientId: p.patientId,
            firstName: p.firstName,
            lastName: p.lastName,
            nickname: p.nickname,
            dateOfBirth: p.dateOfBirth,
            gender: p.gender,
            phoneNumber: p.phoneNumber,
            email: p.email,
            address: p.address,
            bloodType: p.bloodType,
            allergies: p.allergies,
            medicalConditions: p.medicalConditions,
            isActive: p.isActive,
            registrationDate: p.createdAt,
            emergencyContact: p.emergencyContactName && p.emergencyContactPhone ? `${p.emergencyContactName} - ${p.emergencyContactPhone}` : ''
          });
        }
        
        try {
          const apptRes = await AppointmentService.getAppointmentsByPatientId(id);
          const appts = Array.isArray(apptRes.data) ? apptRes.data : [];
          const mapped = appts.map(a => ({
            id: a.appointmentId,
            date: a.appointmentDate ? new Date(a.appointmentDate).toLocaleDateString() : '',
            time: a.startTime,
            duration: a.duration,
            type: a.title || 'Appointment',
            doctor: 'Unknown',
            status: a.status
          }));
          setAppointments(mapped);
        } catch {
          setAppointments([]);
        }
        
        // Fetch medical records from API
        try {
          const medicalRecordsResponse = await MedicalRecordsService.getMedicalRecordsByPatientId(id);
          const records = medicalRecordsResponse.data.map(record => ({
            id: record.recordId,
            date: new Date(record.visitDate).toLocaleDateString(),
            type: record.chiefComplaint,
            doctor: record.doctor ? `Dr. ${record.doctor.lastName}` : 'Unknown',
            diagnosis: record.diagnosis,
            notes: record.notes
          }));
          setMedicalRecords(records);
        } catch (medicalRecordsError) {
          console.error('Error fetching medical records:', medicalRecordsError);
          setMedicalRecords([]);
        }
        
        try {
          const invoicesRes = await PatientService.getPatientBillingRecords(id);
          const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
          const mappedInvoices = invoices.map(inv => ({
            id: inv.invoiceId,
            date: inv.invoiceDate,
            description: inv.description,
            amount: inv.totalAmount,
            status: inv.status
          }));
          setBillingHistory(mappedInvoices);
        } catch {
          setBillingHistory([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching patient data:', error);
        setError('Failed to load patient data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchPatientData();
  }, [id]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleEditPatient = () => {
    navigate(`/patients/${id}/edit`);
  };
  
  const handleDeleteDialogOpen = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  const handleDeletePatient = async () => {
    try {
      // In a real application, you would call your API to delete the patient
      await new Promise(resolve => setTimeout(resolve, 500));
      
      handleDeleteDialogClose();
      navigate('/patients');
    } catch (error) {
      console.error('Error deleting patient:', error);
      alert('Failed to delete patient. Please try again.');
      handleDeleteDialogClose();
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">{error}</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Box>
    );
  }
  
  if (!patient) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Patient not found</Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          component={RouterLink}
          to="/patients"
        >
          Back to Patients
        </Button>
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 1 }}
            component={RouterLink}
            to="/patients"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {`${patient.firstName} ${patient.lastName}`}
          </Typography>
          <Chip 
            label={patient.isActive ? "Active" : "Inactive"}
            color={patient.isActive ? "success" : "default"}
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            sx={{ mr: 1 }}
            onClick={handleEditPatient}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteDialogOpen}
          >
            Delete
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Personal Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary="Gender" secondary={patient.gender} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CakeIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Date of Birth" 
                    secondary={patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString() : 'N/A'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText primary="Phone" secondary={patient.phoneNumber} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText primary="Email" secondary={patient.email} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Address" secondary={patient.address} />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Medical Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List dense>
                <ListItem>
                  <ListItemText primary="Blood Type" secondary={patient.bloodType} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Allergies" secondary={patient.allergies || 'None'} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Medical Conditions" secondary={patient.medicalConditions || 'None'} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Emergency Contact" secondary={patient.emergencyContact} />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Registration Date" 
                    secondary={patient.registrationDate ? new Date(patient.registrationDate).toLocaleDateString() : 'N/A'} 
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper sx={{ width: '100%' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab icon={<EventIcon />} label="Appointments" />
              <Tab icon={<DescriptionIcon />} label="Medical Records" />
              <Tab icon={<ReceiptIcon />} label="Billing History" />
            </Tabs>
            
            <Box sx={{ p: 3 }}>
              {/* Appointments Tab */}
              {tabValue === 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Appointments</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/appointments/new?patientId=${id}`)}
                    >
                      New Appointment
                    </Button>
                  </Box>
                  
                  {appointments.length > 0 ? (
                    appointments.map((appointment) => (
                      <Card key={appointment.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle1">
                                {appointment.type}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {`${appointment.date} at ${appointment.time} (${appointment.duration} min)`}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                              <Typography variant="body2">
                                {appointment.doctor}
                              </Typography>
                              <Chip 
                                label={appointment.status}
                                color={appointment.status === 'Completed' ? 'success' : 'primary'}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                      No appointments found
                    </Typography>
                  )}
                </>
              )}
              
              {/* Medical Records Tab */}
              {tabValue === 1 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Medical Records</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/medical-records/new?patientId=${id}`)}
                    >
                      Add Record
                    </Button>
                  </Box>
                  
                  {medicalRecords.length > 0 ? (
                    medicalRecords.map((record) => (
                      <Card key={record.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle1">
                                {record.type}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(record.date).toLocaleDateString()}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Diagnosis:</strong> {record.diagnosis}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2">
                                <strong>Doctor:</strong> {record.doctor}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Notes:</strong> {record.notes}
                              </Typography>
                              <Box sx={{ mt: 2 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => navigate(`/medical-records/${record.id}`)}
                                  startIcon={<DescriptionIcon />}
                                >
                                  View Details
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                      No medical records found
                    </Typography>
                  )}
                </>
              )}
              
              {/* Billing History Tab */}
              {tabValue === 2 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Billing History</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/billing/new?patientId=${id}`)}
                    >
                      New Invoice
                    </Button>
                  </Box>
                  
                  {billingHistory.length > 0 ? (
                    billingHistory.map((bill) => (
                      <Card key={bill.id} sx={{ mb: 2 }}>
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="subtitle1">
                                {bill.description}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(bill.date).toLocaleDateString()}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'flex-end' } }}>
                              <Typography variant="subtitle1">
                                ${bill.amount.toFixed(2)}
                              </Typography>
                              <Chip 
                                label={bill.status}
                                color={bill.status === 'Paid' ? 'success' : bill.status === 'Pending' ? 'warning' : 'error'}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    ))
                  ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                      No billing history found
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Patient</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {patient.firstName} {patient.lastName}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeletePatient} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientDetails;
